import React, { useState, useMemo } from "react";
import { RefreshCw, X, ArrowRight, DollarSign } from "lucide-react";
import { Modal, MODAL_HEADER, MODAL_ACTIONS } from "../shared/Modal";
import { FormField } from "../shared/FormField";
import * as api from "../../api";
import { useAppContext } from "../../AppContext";

/* -----------------------------------------------------------------------------
 * AdjustmentRequestModal — admin flow: pick a new package + note for a client
 * who can't receive the item they originally bought (e.g. Free Fire Pase de
 * Nivel already consumed). Once submitted, the order flips to "Ajuste
 * Pendiente" and the client sees a banner to pay the difference.
 *
 * Owned by App.js: `adjustOrderModal` (the target order) + setter. When
 * `adjustOrderModal` is null the modal is not rendered.
 * --------------------------------------------------------------------------- */
export function AdjustmentRequestModal() {
  const ctx = useAppContext();
  const {
    adjustOrderModal, setAdjustOrderModal, games, usdToBs, formatBs, formatUsd,
    showToast, loadOrders,
  } = ctx;

  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [priceOverrideUsd, setPriceOverrideUsd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset the internal form whenever a new target order is opened so we
  // never leak values from a previous adjustment session.
  React.useEffect(() => {
    if (adjustOrderModal) {
      const items = adjustOrderModal.items || [];
      setSelectedItemId(items.length ? items[0].id : "");
      setSelectedGameId(adjustOrderModal.gameId || "");
      setSelectedPackageId("");
      setPriceOverrideUsd("");
      setNote("");
    }
  }, [adjustOrderModal]);

  const selectedGame = useMemo(
    () => games?.find((g) => g.id === selectedGameId) || null,
    [games, selectedGameId]
  );
  const selectedPackage = useMemo(
    () => (selectedGame?.packages || []).find((p) => p.id === selectedPackageId) || null,
    [selectedGame, selectedPackageId]
  );
  const newPriceUsd = useMemo(() => {
    const override = parseFloat(priceOverrideUsd);
    if (!Number.isNaN(override) && override > 0) return override;
    return Number(selectedPackage?.priceUsd || 0);
  }, [selectedPackage, priceOverrideUsd]);
  const targetItem = useMemo(
    () => (adjustOrderModal?.items || []).find((i) => i.id === selectedItemId)
      || (adjustOrderModal?.items || [])[0] || null,
    [adjustOrderModal, selectedItemId]
  );
  const originalPriceUsd = Number(targetItem?.priceUsd ?? adjustOrderModal?.priceUsd ?? 0);
  const differenceUsd = Math.round((newPriceUsd - originalPriceUsd) * 100) / 100;
  const differenceBs = usdToBs(differenceUsd);

  if (!adjustOrderModal) return null;

  const submit = async () => {
    if (!selectedPackageId) {
      showToast("Falta paquete", "Elige el nuevo paquete de reemplazo.", "error");
      return;
    }
    if (note.trim().length < 5) {
      showToast("Falta nota", "Escribe una nota explicativa (mín. 5 caracteres).", "error");
      return;
    }
    setBusy(true);
    try {
      await api.requestOrderAdjustment(adjustOrderModal.id, {
        newGameId: selectedGameId,
        newPackageId: selectedPackageId,
        itemId: selectedItemId || (targetItem?.id ?? null),
        newPriceUsd: priceOverrideUsd ? Number(priceOverrideUsd) : null,
        note: note.trim(),
      });
      // Close first to avoid stale-state race; then refresh the orders list.
      setAdjustOrderModal(null);
      await loadOrders();
      showToast("Ajuste solicitado", "El cliente verá el aviso en su Historial.", "success");
    } catch (err) {
      showToast("Error", err.message || "No se pudo solicitar el ajuste", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={() => !busy && setAdjustOrderModal(null)} size="lg" testId="adjust-order-modal">
      <div className="space-y-4">
        <div className={MODAL_HEADER}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-amber-950/60 border border-amber-500 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Ajustar Pedido</h3>
              <p className="text-[10px] font-mono text-zinc-500">{adjustOrderModal.id} · {adjustOrderModal.gameName}</p>
            </div>
          </div>
          <button data-testid="adjustment-request-modal-button-1" onClick={() => !busy && setAdjustOrderModal(null)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full">
            <X className="w-4 h-4" />
          </button>
        </div>

        {(adjustOrderModal.items || []).length > 1 && (
          <FormField label="¿Cuál recarga se reajusta?">
            <select
              data-testid="adjust-target-item-select"
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-amber-500">
              {(adjustOrderModal.items || []).map((it, idx) => (
                <option key={it.id} value={it.id}>
                  #{idx + 1} · {it.gameName} · {it.packageName}
                </option>
              ))}
            </select>
          </FormField>
        )}

        <div className="p-3 rounded-xl bg-zinc-950 border border-zinc-800 text-xs space-y-1 font-mono">
          <p><span className="text-zinc-500">Original:</span> <strong className="text-white">{targetItem?.packageName || adjustOrderModal.packageName}</strong></p>
          <p><span className="text-zinc-500">Precio original:</span> <strong className="text-green-400">{formatUsd(originalPriceUsd)}</strong> · <strong>{formatBs(targetItem?.priceBs ?? adjustOrderModal.priceBs ?? 0)}</strong></p>
          <p className="text-[10px] text-zinc-500">El resto del pedido queda igual. El cliente deberá ACEPTAR o RECHAZAR esta propuesta.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Nuevo Juego">
            <select
              data-testid="adjust-new-game-select"
              value={selectedGameId}
              onChange={(e) => { setSelectedGameId(e.target.value); setSelectedPackageId(""); }}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-amber-500">
              {games?.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Nuevo Paquete">
            <select
              data-testid="adjust-new-package-select"
              value={selectedPackageId}
              onChange={(e) => setSelectedPackageId(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-amber-500">
              <option value="">— Elegir paquete —</option>
              {(selectedGame?.packages || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name} — {formatUsd(Number(p.priceUsd || 0))}</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Precio USD del nuevo paquete (opcional — override manual)">
          <input
            type="number"
            step="0.01"
            data-testid="adjust-price-override-input"
            value={priceOverrideUsd}
            onChange={(e) => setPriceOverrideUsd(e.target.value)}
            placeholder={selectedPackage ? String(selectedPackage.priceUsd) : "0.00"}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-amber-500"
          />
        </FormField>

        {selectedPackageId && (
          <div className={`p-3 rounded-xl border space-y-1 text-xs ${
            differenceUsd < 0
              ? "bg-emerald-950/30 border-emerald-700/60"
              : differenceUsd > 0
                ? "bg-amber-950/30 border-amber-700/60"
                : "bg-zinc-900 border-zinc-700"
          }`}>
            <div className="flex items-center justify-between font-mono">
              <span className={differenceUsd < 0 ? "text-emerald-100" : "text-amber-100"}>
                {differenceUsd < 0 ? "Reembolso al cliente:" : differenceUsd > 0 ? "Diferencia a pagar:" : "Sin diferencia:"}
              </span>
              <span className="flex items-center gap-2">
                <DollarSign className={`w-3.5 h-3.5 ${differenceUsd < 0 ? "text-emerald-300" : "text-amber-300"}`} />
                <strong className={differenceUsd < 0 ? "text-emerald-100" : "text-amber-100"} data-testid="adjust-diff-usd">{formatUsd(Math.abs(differenceUsd))}</strong>
                <ArrowRight className={`w-3.5 h-3.5 ${differenceUsd < 0 ? "text-emerald-400" : "text-amber-400"}`} />
                <strong className={differenceUsd < 0 ? "text-emerald-200" : "text-amber-200"} data-testid="adjust-diff-bs">{formatBs(Math.abs(differenceBs))}</strong>
              </span>
            </div>
            {differenceUsd < 0 && (
              <p className="text-[10px] text-emerald-300/90" data-testid="adjust-downgrade-hint">
                El cliente pagó de más. Al confirmar, este monto se acreditará automáticamente a su <strong>Saldo a Favor</strong> y el pedido pasará a <strong>Completado</strong>.
              </p>
            )}
            {Math.abs(differenceUsd) <= 0.005 && (
              <p className="text-[10px] text-zinc-400" data-testid="adjust-neutral-hint">
                Mismo precio — solo se sustituye el paquete y el pedido pasa a <strong>Completado</strong>.
              </p>
            )}
          </div>
        )}

        <FormField label="Nota explicativa para el cliente (visible en su Historial)">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="adjust-note-input"
            rows={3}
            maxLength={800}
            placeholder="Ej. Pase de Nivel ya consumido en la cuenta. Reemplazo por Paquete de Diamantes."
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 resize-none"
          />
          <p className="text-[10px] font-mono text-zinc-500 mt-1">{note.length}/800</p>
        </FormField>

        <div className={MODAL_ACTIONS}>
          <button data-testid="adjustment-request-modal-button-2"
            type="button"
            onClick={() => setAdjustOrderModal(null)}
            disabled={busy}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40">
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !selectedPackageId || note.trim().length < 5}
            data-testid="confirm-adjust-order-btn"
            className="flex-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span>{busy ? "Enviando..." : "Solicitar Ajuste"}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
